import type {
  Service, ServiceStatus, DeploymentType, Instrumentation, Environment,
} from "@coremonit/types";
import { makeRng, int, pick, chance } from "./rng";

// Realistic service catalog — mixed languages, environments, deployment
// topologies. Matches how a real observability tenant's inventory looks.
const CATALOG: Array<{
  name: string; language: string; runtime: string; team: string;
  tags: string[]; kind: "frontend" | "gateway" | "auth" | "backend" | "datastore" | "cache" | "queue" | "external";
}> = [
  { name: "web-storefront",     language: "TypeScript", runtime: "Node 20 / Next.js 14",   team: "web-platform",  tags: ["frontend", "critical"], kind: "frontend" },
  { name: "mobile-bff",         language: "Kotlin",     runtime: "JVM 21",                  team: "mobile",         tags: ["bff"],                  kind: "gateway" },
  { name: "api-gateway",        language: "Go",         runtime: "Go 1.22",                 team: "platform",       tags: ["gateway", "critical"],  kind: "gateway" },
  { name: "auth-service",       language: "Rust",       runtime: "Actix",                   team: "identity",       tags: ["auth", "critical"],     kind: "auth" },
  { name: "user-service",       language: "Java",       runtime: "JDK 21 / Spring Boot 3",  team: "platform",       tags: ["core"],                 kind: "backend" },
  { name: "order-service",      language: "Java",       runtime: "JDK 21 / Spring Boot 3",  team: "commerce",       tags: ["core", "critical"],     kind: "backend" },
  { name: "payment-service",    language: "Go",         runtime: "Go 1.22",                 team: "payments",       tags: ["pci", "critical"],      kind: "backend" },
  { name: "inventory-service",  language: "Python",     runtime: "Python 3.12 / FastAPI",   team: "commerce",       tags: ["core"],                 kind: "backend" },
  { name: "search-service",     language: "Go",         runtime: "Go 1.22",                 team: "search",         tags: ["core"],                 kind: "backend" },
  { name: "notification-service",language:"Node.js",    runtime: "Node 20",                 team: "growth",         tags: ["core"],                 kind: "backend" },
  { name: "email-worker",       language: "Node.js",    runtime: "Node 20",                 team: "growth",         tags: ["worker"],               kind: "backend" },
  { name: "billing-service",    language: "Ruby",       runtime: "Ruby 3.3 / Rails 7",      team: "billing",        tags: ["core"],                 kind: "backend" },
  { name: "reporting-service",  language: "Python",     runtime: "Python 3.12",             team: "data",           tags: ["batch"],                kind: "backend" },
  { name: "recommendation-svc", language: "Python",     runtime: "Python 3.12",             team: "ml",             tags: ["ml"],                   kind: "backend" },
  { name: "session-cache",      language: "Redis",      runtime: "Redis 7",                 team: "platform",       tags: ["cache"],                kind: "cache" },
  { name: "postgres-primary",   language: "PostgreSQL", runtime: "Postgres 16",             team: "dbre",           tags: ["db", "critical"],       kind: "datastore" },
  { name: "postgres-replica",   language: "PostgreSQL", runtime: "Postgres 16",             team: "dbre",           tags: ["db"],                   kind: "datastore" },
  { name: "kafka-events",       language: "Kafka",      runtime: "Kafka 3.7",               team: "platform",       tags: ["queue"],                kind: "queue" },
  { name: "stripe-webhook",     language: "n/a",        runtime: "External SaaS",           team: "payments",       tags: ["external"],             kind: "external" },
  { name: "sendgrid-webhook",   language: "n/a",        runtime: "External SaaS",           team: "growth",         tags: ["external"],             kind: "external" },
];

const ENVS: Environment[] = ["production", "staging", "development", "uat"];
const DEPLOY_TYPES: DeploymentType[] = ["kubernetes", "docker", "ecs", "eks", "aks", "gke", "vm"];
const INSTR: Instrumentation[] = ["opentelemetry", "prometheus", "jaeger", "grafana-agent", "datadog-agent"];
const REGIONS = ["us-east-1", "us-west-2", "eu-west-1", "ap-south-1"];
const CLUSTERS = ["prod-eks-01", "prod-eks-02", "stage-eks-01", "dev-eks-01"];
const NAMESPACES = ["default", "core", "commerce", "identity", "payments", "growth", "search", "data", "platform"];
const OWNERS = ["alice.wong", "bob.chen", "carol.silva", "diego.perez", "erin.zhao", "farida.ahmed", "gabe.martins", "hana.tan"];

// Distribute status so we get a realistic mix.
function statusFor(rng: () => number, kind: string): ServiceStatus {
  const r = rng();
  if (kind === "external") return r < 0.15 ? "warning" : "healthy";
  if (kind === "datastore") return r < 0.08 ? "warning" : "healthy";
  if (r < 0.06) return "critical";
  if (r < 0.16) return "warning";
  if (r < 0.20) return "unknown";
  return "healthy";
}

const now = () => new Date().toISOString();

export function generateServices(seed = 42): Service[] {
  const rng = makeRng(seed);
  return CATALOG.map((c, i): Service => {
    const status = statusFor(rng, c.kind);
    const p50 = c.kind === "datastore" ? int(rng, 5, 25) : int(rng, 30, 220);
    const p95 = p50 * (2 + rng() * 2);
    const p99 = p95 * (1.4 + rng() * 0.9);
    const err = status === "critical" ? 3 + rng() * 7
               : status === "warning" ? 0.6 + rng() * 1.4
               : rng() * 0.4;
    const desired = c.kind === "external" ? 0 : int(rng, 2, 12);
    const available = status === "critical" ? Math.max(0, desired - int(rng, 1, desired))
                    : status === "warning" ? Math.max(1, desired - int(rng, 0, 2))
                    : desired;
    const env = c.tags.includes("critical") ? "production" : pick(rng, ENVS);
    return {
      id: `svc_${(i + 1).toString().padStart(3, "0")}`,
      name: c.name,
      slug: c.name,
      description: `${c.name} — owned by ${c.team}`,
      owner: pick(rng, OWNERS),
      team: c.team,
      tags: c.tags,
      environment: env,
      language: c.language,
      runtime: c.runtime,
      version: `${int(rng, 1, 4)}.${int(rng, 0, 20)}.${int(rng, 0, 40)}`,
      status,
      deployment: {
        type: pick(rng, DEPLOY_TYPES),
        cluster: pick(rng, CLUSTERS),
        namespace: pick(rng, NAMESPACES),
        region: pick(rng, REGIONS),
      },
      instrumentation: [pick(rng, INSTR), ...(chance(rng, 30) ? [pick(rng, INSTR)] : [])],
      createdAt: new Date(Date.now() - int(rng, 30, 720) * 86400000).toISOString(),
      updatedAt: now(),
      latencyP50Ms: Math.round(p50),
      latencyP95Ms: Math.round(p95),
      latencyP99Ms: Math.round(p99),
      errorRatePct: +err.toFixed(2),
      requestsPerMin: int(rng, 40, 12000),
      availabilityPct: status === "critical" ? +(97 + rng() * 1.5).toFixed(2)
                     : status === "warning" ? +(98.5 + rng() * 0.9).toFixed(2)
                     : +(99.5 + rng() * 0.49).toFixed(2),
      replicas: { desired, available },
      restarts24h: status === "critical" ? int(rng, 4, 22) : status === "warning" ? int(rng, 1, 5) : int(rng, 0, 1),
      oomKills24h: status === "critical" ? int(rng, 1, 6) : chance(rng, 15) ? 1 : 0,
    };
  });
}

// Static kind map (used by dependency graph — must match generateServices order).
export const SERVICE_KIND: Record<string, string> = Object.fromEntries(
  CATALOG.map((c, i) => [`svc_${(i + 1).toString().padStart(3, "0")}`, c.kind]),
);
